// utils/informeOrganizacionesPdf.js
// PDF del Informe de Organizaciones generado con jsPDF y TEXTO REAL (no
// html2canvas). Antes se rasterizaba toda la tabla con html2canvas en un único
// canvas: en Safari/Firefox, cuando la tabla es larga, el canvas supera el
// límite de área del navegador y queda EN BLANCO, y solo se veían el encabezado
// y el pie (que van como imágenes aparte). Con texto real eso no puede pasar,
// además sale nítido al imprimir. Mismo criterio que utils/informePdf.js.
import jsPDF from 'jspdf';
import encabezadoImg from '../components/informes/encabezado_A4.png';
import pieImg from '../components/informes/pie_hoja_A4.png';

const ANCHO_PAGINA = 297; // A4 apaisado, mm
const ALTO_PAGINA = 210;
const MARGEN_H = 10;
const MARGEN_TOP = 10;
const MARGEN_BOTTOM = 10;
const ANCHO_UTIL = ANCHO_PAGINA - 2 * MARGEN_H; // 277mm

const PAD_H = 1.5;
const PAD_V = 1.2;
const LINE_H = 3.4;
const ALTO_FILA_MIN = 7;

// Paleta igual a la de la app (teal del encabezado de tabla)
const TEAL_OSCURO = [15, 118, 110];
const TEAL_CLARO = [240, 253, 250];
const GRIS_TEXTO = [55, 65, 81];
const GRIS_SUAVE = [107, 114, 128];
const GRIS_BORDE = [203, 213, 225];

const ALTO_METRICAS = 17;
const GAP_METRICAS = 5;

// Columnas del PDF. Suman exactamente ANCHO_UTIL (277mm).
const COLUMNAS_FULL = [
	{ id: 'organizacion', label: 'Organización', ancho: 58 },
	{ id: 'contacto', label: 'Contacto ONG', ancho: 42 },
	{ id: 'categoria', label: 'Categoría', ancho: 24 },
	{ id: 'terminal', label: 'Terminal Política', ancho: 30 },
	{ id: 'expediente', label: 'Exp. de Proyecto', ancho: 30 },
	{ id: 'monto', label: 'Monto', ancho: 33 },
	{ id: 'tipo', label: 'Tipo de obra', ancho: 26 },
	{ id: 'ubicacion', label: 'Ubicación', ancho: 34 },
];

const COLUMNAS_SUBTOTALES = [
	{ id: 'organizacion', label: 'Organización', ancho: 90 },
	{ id: 'contacto', label: 'Contacto ONG', ancho: 70 },
	{ id: 'categoria', label: 'Categoría', ancho: 35 },
	{ id: 'terminal', label: 'Terminal Política', ancho: 45 },
	{ id: 'monto', label: 'Monto', ancho: 37 },
];

// --- Helpers de formato (idénticos a la pantalla) ---
const cargarImagenComoDataUrl = (src) =>
	new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			try {
				const c = document.createElement('canvas');
				c.width = img.naturalWidth;
				c.height = img.naturalHeight;
				c.getContext('2d').drawImage(img, 0, 0);
				resolve({ dataUrl: c.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
			} catch (e) {
				resolve(null);
			}
		};
		img.onerror = () => resolve(null);
		img.src = src;
	});

const formatearMonto = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return '$ 0,00';
	return '$' + n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatearEntero = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return '0';
	return n.toLocaleString('es-AR');
};

const getMontoConveniado = (obra) => Number(obra?.monto_obra || 0) + Number(obra?.adendas || 0);

const getTipoObra = (obra) => {
	const ts = obra?.array_tipo_subtipo_proyecto || [];
	if (Array.isArray(ts) && ts.length > 0) {
		return ts.map((item) => {
			const label = String(item?.subtipo || '').trim() || String(item?.tipo || '').trim() || 'Proyecto';
			const cantidad = Number(item?.cantidad || 0);
			return cantidad > 0 ? `${formatearEntero(cantidad)} ${label}` : label;
		});
	}
	if (obra?.area_responsable) return [obra.area_responsable];
	return ['—'];
};

const getUbicacion = (obra) => {
	const partido = String(obra?.partido_nombre || '').trim();
	const localidad = String(obra?.lc_21_localidad || '').trim();
	if (partido && localidad && partido.toLowerCase() !== localidad.toLowerCase()) {
		return `${partido} — ${localidad}`;
	}
	return partido || localidad || '—';
};

const getExpedienteProyecto = (obra) => {
	const proyecto = (obra?.array_obras_expediente || []).find((item) => item && item.objeto_o === 'Proyecto');
	return proyecto?.expediente_o || 'Sin especificar';
};

const contactosOngPartes = (contactos) => {
	const lista = Array.isArray(contactos) ? contactos : [];
	return lista
		.map((c) => {
			const nombre = String(c?.nombre || '').trim();
			const tel = String(c?.telefono || '').trim();
			const email = String(c?.email || '').trim();
			const canales = [tel ? `Tel: ${tel}` : '', email].filter(Boolean).join(' · ');
			if (!nombre && !canales) return '';
			return canales ? `${nombre || 'Sin nombre'} (${canales})` : nombre;
		})
		.filter(Boolean);
};

const linea = (text, opts = {}) => ({
	text: String(text ?? ''),
	bold: Boolean(opts.bold),
	size: opts.size ?? 7.2,
	color: opts.color ?? GRIS_TEXTO,
	align: opts.align ?? 'left',
});

// Envuelve un texto a varias líneas según el ancho (con la fuente/size dados).
const envolver = (doc, text, anchoMM, opts = {}) => {
	doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
	doc.setFontSize(opts.size ?? 7.2);
	const partes = doc.splitTextToSize(String(text ?? ''), anchoMM) || [];
	return partes.map((t) => linea(t, opts));
};

// Construye las líneas de cada celda de una fila. Devuelve { celdas, alto }.
const construirFila = (doc, columnas, { grupo, obra }) => {
	const celdas = {};

	for (const col of columnas) {
		const w = col.ancho - 2 * PAD_H;
		if (col.id === 'organizacion') {
			if (obra) {
				const exp = String(obra?.id_partido_obra || '').trim();
				const nombre = String(obra?.nombre_obra || '').trim();
				const sub = exp + (nombre ? ` · ${nombre}` : '');
				celdas[col.id] = [
					...envolver(doc, grupo.organizacion, w, { bold: true, size: 7.8 }),
					...(sub ? envolver(doc, sub, w, { size: 6.3, color: GRIS_SUAVE }) : []),
				];
			} else {
				const n = grupo.obras.length;
				const label = `Subtotal ${grupo.organizacion} (${n} ${n === 1 ? 'obra' : 'obras'})`;
				celdas[col.id] = envolver(doc, label, w, { bold: true, size: 7.8 });
			}
		} else if (col.id === 'contacto') {
			const partes = contactosOngPartes(grupo.ong_contactos);
			celdas[col.id] = partes.length
				? partes.flatMap((p) => envolver(doc, p, w, { size: 6.3 }))
				: [linea('—', { size: 6.3 })];
		} else if (col.id === 'categoria') {
			celdas[col.id] = envolver(doc, grupo.ong_categoria || '—', w, { size: 6.5 });
		} else if (col.id === 'terminal') {
			celdas[col.id] = envolver(doc, grupo.ong_terminal_politica || '—', w, { size: 6.5 });
		} else if (col.id === 'expediente') {
			celdas[col.id] = obra ? envolver(doc, getExpedienteProyecto(obra), w, { size: 6.3 }) : [];
		} else if (col.id === 'monto') {
			if (obra) {
				const monto = Number(obra?.monto_obra || 0);
				const adendas = Number(obra?.adendas || 0);
				if (adendas > 0) {
					celdas[col.id] = [
						linea(formatearMonto(monto), { bold: true, size: 7.4, align: 'right' }),
						linea(`+ adenda por ${formatearMonto(adendas)}`, { size: 6, color: GRIS_SUAVE, align: 'right' }),
						linea(`Total: ${formatearMonto(monto + adendas)}`, { bold: true, size: 6.3, align: 'right' }),
					];
				} else {
					celdas[col.id] = [linea(formatearMonto(monto), { size: 7.4, align: 'right' })];
				}
			} else {
				const subtotal = grupo.obras.reduce((acc, o) => acc + getMontoConveniado(o), 0);
				celdas[col.id] = [linea(formatearMonto(subtotal), { bold: true, size: 7.8, align: 'right' })];
			}
		} else if (col.id === 'tipo') {
			celdas[col.id] = obra
				? getTipoObra(obra).flatMap((t) => envolver(doc, t, w, { size: 6.3 }))
				: [];
		} else if (col.id === 'ubicacion') {
			celdas[col.id] = obra ? envolver(doc, getUbicacion(obra), w, { size: 6.5 }) : [];
		} else {
			celdas[col.id] = [];
		}
	}

	const maxLineas = Math.max(1, ...Object.values(celdas).map((l) => l.length));
	const alto = Math.max(ALTO_FILA_MIN, maxLineas * LINE_H + 2 * PAD_V);
	return { celdas, alto };
};

export const generarInformeOrganizacionesPdf = async ({
	grupos,
	totalInversion,
	cantidadObras,
	cantidadOrganizaciones,
	categoriaLabel,
	partidoLabel,
	yearsLabel,
	q,
	soloSubtotales,
	nombreArchivo,
}) => {
	if (!grupos || grupos.length === 0) {
		throw new Error('No hay datos para generar el PDF');
	}

	const doc = new jsPDF({ orientation: 'l', unit: 'mm', format: 'a4' });
	const columnas = soloSubtotales ? COLUMNAS_SUBTOTALES : COLUMNAS_FULL;

	const [encabezado, pie] = await Promise.all([
		cargarImagenComoDataUrl(encabezadoImg),
		cargarImagenComoDataUrl(pieImg),
	]);

	let pieAncho = 0;
	let pieAlto = 0;
	if (pie) {
		pieAlto = Math.min(22, ANCHO_UTIL * (pie.height / pie.width));
		pieAncho = pieAlto * (pie.width / pie.height);
	}

	const yEncabezado = MARGEN_TOP;
	const encabezadoAlto = encabezado ? ANCHO_UTIL * (encabezado.height / encabezado.width) : 0;

	// Títulos (texto, no imagen). Sin el título "Gestión Axel Kicillof." por
	// pedido: queda como encabezado principal "Organizaciones: ...".
	const SUBTITULO_1 = `Organizaciones: ${categoriaLabel}`;
	const lineasSubtitulo2 = [
		'Informe de intervenciones complementarias del hábitat',
		partidoLabel ? `partido: ${partidoLabel}` : '',
		yearsLabel ? `años: ${yearsLabel}` : '',
		q ? `búsqueda: "${q}"` : '',
		soloSubtotales ? 'solo subtotales por ONG' : '',
	].filter(Boolean).join(' — ');

	const gapTitulo = 4;

	const yTrasSubtitulo = yEncabezado + encabezadoAlto + gapTitulo + 6 + 6 + 3;
	const yTablaPrimera = yTrasSubtitulo + ALTO_METRICAS + GAP_METRICAS;
	const yTablaSiguientes = yTrasSubtitulo;

	const yPieTop = ALTO_PAGINA - MARGEN_BOTTOM - pieAlto;
	const yPieGap = pieAlto > 0 ? 2 : 0;
	const yLimiteContenido = yPieTop - yPieGap;

	const altoHeaderTabla = 7.5;
	let y = yTablaPrimera;

	const dibujarMetricas = () => {
		const gap = 4;
		const anchoTarjeta = (ANCHO_UTIL - 2 * gap) / 3;
		const metricas = [
			{ label: 'Inversión total', valor: formatearMonto(totalInversion) },
			{ label: 'Cantidad de obras', valor: formatearEntero(cantidadObras) },
			{ label: 'Organizaciones', valor: formatearEntero(cantidadOrganizaciones) },
		];
		metricas.forEach((m, i) => {
			const x = MARGEN_H + i * (anchoTarjeta + gap);
			doc.setDrawColor(...TEAL_OSCURO);
			doc.setLineWidth(0.5);
			doc.roundedRect(x, yTrasSubtitulo, anchoTarjeta, ALTO_METRICAS, 1.5, 1.5, 'S');
			doc.setFont('helvetica', 'bold');
			doc.setFontSize(7);
			doc.setTextColor(...TEAL_OSCURO);
			doc.text(m.label.toUpperCase(), x + anchoTarjeta / 2, yTrasSubtitulo + 6.5, { align: 'center' });
			doc.setFontSize(13);
			doc.setTextColor(17, 24, 39);
			doc.text(m.valor, x + anchoTarjeta / 2, yTrasSubtitulo + 14, { align: 'center' });
		});
	};

	const dibujarEncabezadoPagina = (esPrimera) => {
		if (encabezado) {
			doc.addImage(encabezado.dataUrl, 'PNG', MARGEN_H, yEncabezado, ANCHO_UTIL, encabezadoAlto);
		}
		const yTitulo = yEncabezado + encabezadoAlto + gapTitulo + 6;
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(13);
		doc.setTextColor(...TEAL_OSCURO);
		doc.text(SUBTITULO_1, ANCHO_PAGINA / 2, yTitulo, { align: 'center' });

		doc.setFont('helvetica', 'normal');
		doc.setFontSize(8);
		doc.setTextColor(...GRIS_TEXTO);
		doc.text(lineasSubtitulo2, ANCHO_PAGINA / 2, yTitulo + 6, { align: 'center' });

		if (esPrimera) dibujarMetricas();
		if (pie) {
			doc.addImage(pie.dataUrl, 'PNG', (ANCHO_PAGINA - pieAncho) / 2, yPieTop, pieAncho, pieAlto);
		}
	};

	const dibujarHeaderTabla = () => {
		doc.setFillColor(...TEAL_OSCURO);
		doc.rect(MARGEN_H, y, ANCHO_UTIL, altoHeaderTabla, 'F');
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(7.6);
		doc.setTextColor(255, 255, 255);
		const lineHeightHeader = 3.3;
		let x = MARGEN_H;
		for (const col of columnas) {
			const lineas = doc.splitTextToSize(col.label, col.ancho - 2 * PAD_H);
			const altoTextoHeader = lineas.length * lineHeightHeader;
			const yIni = y + (altoHeaderTabla - altoTextoHeader) / 2 + lineHeightHeader - 0.6;
			for (let i = 0; i < lineas.length; i++) {
				doc.text(lineas[i], x + PAD_H, yIni + i * lineHeightHeader);
			}
			x += col.ancho;
		}
		y += altoHeaderTabla;
	};

	const nuevaPagina = () => {
		doc.addPage();
		y = yTablaSiguientes;
		dibujarEncabezadoPagina(false);
		dibujarHeaderTabla();
	};

	// Dibuja una fila (celdas) en la posición actual
	const dibujarFila = ({ celdas, alto }, conFondo) => {
		if (conFondo) {
			doc.setFillColor(...TEAL_CLARO);
			doc.rect(MARGEN_H, y, ANCHO_UTIL, alto, 'F');
		}
		// bordes verticales + marco
		doc.setDrawColor(...GRIS_BORDE);
		doc.setLineWidth(0.15);
		let xBorde = MARGEN_H;
		for (const col of columnas) {
			xBorde += col.ancho;
			if (col !== columnas[columnas.length - 1]) doc.line(xBorde, y, xBorde, y + alto);
		}
		doc.setDrawColor(...GRIS_BORDE);
		doc.rect(MARGEN_H, y, ANCHO_UTIL, alto, 'S');

		let x = MARGEN_H;
		for (const col of columnas) {
			const lineas = celdas[col.id] || [];
			const yBase = y + PAD_V + LINE_H - 0.9;
			const xCol = x;
			lineas.forEach((l, i) => {
				doc.setFont('helvetica', l.bold ? 'bold' : 'normal');
				doc.setFontSize(l.size);
				doc.setTextColor(...l.color);
				const xx = l.align === 'right' ? xCol + col.ancho - PAD_H : xCol + PAD_H;
				doc.text(l.text, xx, yBase + i * LINE_H, { align: l.align });
			});
			x += col.ancho;
		}
		y += alto;
	};

	dibujarEncabezadoPagina(true);
	dibujarHeaderTabla();

	let indiceFila = 0;

	const dibujarFilaConPaginado = (fila) => {
		if (y + fila.alto > yLimiteContenido) nuevaPagina();
		dibujarFila(fila, indiceFila % 2 === 1);
		indiceFila += 1;
	};

	for (const grupo of grupos) {
		if (!soloSubtotales) {
			for (const obra of grupo.obras) {
				dibujarFilaConPaginado(construirFila(doc, columnas, { grupo, obra }));
			}
		}
		dibujarFilaConPaginado(construirFila(doc, columnas, { grupo, obra: null }));
	}

	// --- Total general ---
	if (y + ALTO_FILA_MIN > yLimiteContenido) nuevaPagina();
	const montoCol = columnas.find((c) => c.id === 'monto');
	const xMonto = MARGEN_H + columnas.slice(0, columnas.indexOf(montoCol)).reduce((a, c) => a + c.ancho, 0);

	doc.setFillColor(...TEAL_OSCURO);
	doc.rect(MARGEN_H, y, ANCHO_UTIL, ALTO_FILA_MIN, 'F');
	doc.setFont('helvetica', 'bold');
	doc.setFontSize(8);
	doc.setTextColor(255, 255, 255);
	doc.text('Total general', xMonto - PAD_H, y + ALTO_FILA_MIN - 2.4, { align: 'right' });
	doc.text(formatearMonto(totalInversion), xMonto + montoCol.ancho - PAD_H, y + ALTO_FILA_MIN - 2.4, { align: 'right' });
	y += ALTO_FILA_MIN;

	// Pie de texto (fecha de salida), arriba del pie imagen
	doc.setFont('helvetica', 'normal');
	doc.setFontSize(8);
	doc.setTextColor(...GRIS_SUAVE);
	doc.text('Dirección Provincial de Intervenciones Complementarias', MARGEN_H, y + 6);
	doc.text(`Fecha de salida: ${new Date().toLocaleDateString('es-AR')}`, ANCHO_PAGINA - MARGEN_H, y + 6, { align: 'right' });

	doc.save(`${nombreArchivo || 'informe_organizaciones'}.pdf`);
};
